import { useCallback, useEffect, useRef } from 'react';
import { discoveryApi } from '../../api/discovery';
import { readHomeSnapshot, writeHomeSnapshot, homeSnapshotGeneration } from './homeSnapshot';
import { readDiscoveryState } from '../../features/discovery/storage';

// Claims are only a prefetch hint, never authentication. A response is handed
// over only after /auth/me confirms the same account and token is unchanged.
export function warmupAccount(token) {
  try {
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return Number.isSafeInteger(payload.userId) &&
      payload.userId > 0 &&
      Number.isFinite(payload.exp) &&
      payload.exp * 1000 > Date.now()
      ? payload.userId
      : null;
  } catch {
    return null;
  }
}
export const homeDiscoveryRequest = async (userId, signal, locale) => {
  const token = localStorage.getItem('POSTPOP_AUTH_TOKEN');
  const generation = homeSnapshotGeneration();
  const data = await discoveryApi.albums({
    authenticated: Boolean(userId),
    signal,
    albumSize: 36,
    includeSupportingContent: false,
    includeTracks: true,
    retain: readDiscoveryState(userId ? `user_${userId}` : 'guest').deck.ids,
  });
  if (!signal.aborted) writeHomeSnapshot(userId, locale, token, data, generation);
  return data;
};

export default function useHomeWarmup({ authReady, locale }) {
  const pending = useRef(null);
  const token = localStorage.getItem('POSTPOP_AUTH_TOKEN');
  const readiness = useRef(authReady);
  readiness.current = authReady;
  useEffect(() => {
    const userId = warmupAccount(token);
    if (readiness.current || !userId || readHomeSnapshot(userId, locale, token)) return undefined;
    const controller = new AbortController();
    const request = {
      token,
      userId,
      locale,
      controller,
      promise: homeDiscoveryRequest(userId, controller.signal, locale),
    };
    pending.current = request;
    // Auth can fail before a consumer exists. Do not leave a rejected promise.
    request.promise.catch(() => {});
    return () => {
      controller.abort();
      if (pending.current === request) pending.current = null;
    };
  }, [token, locale]);
  return useCallback(
    (userId, signal) => {
      const request = pending.current;
      if (
        !request ||
        request.userId !== userId ||
        request.locale !== locale ||
        request.token !== localStorage.getItem('POSTPOP_AUTH_TOKEN') ||
        request.controller.signal.aborted
      ) {
        if (request) {
          request.controller.abort();
          pending.current = null;
        }
        return homeDiscoveryRequest(userId, signal, locale);
      }
      pending.current = null;
      const abort = () => request.controller.abort();
      if (signal.aborted) abort();
      else signal.addEventListener('abort', abort, { once: true });
      return request.promise.finally(() => signal.removeEventListener('abort', abort));
    },
    [locale],
  );
}
